import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { retryImport } from "@/components/ImportRetry";

const ago = (iso) => {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return `${h} hour${h === 1 ? "" : "s"} ago`;
};

const label = (j) => (j.committed ? "Saved" : j.status === "fetching" ? "Still fetching" : j.status === "error" ? "Failed" : "Ready to save");
const tone = (j) => (j.committed ? "text-emerald-700" : j.status === "error" ? "text-red-600" : "text-rf-orange");

export default function MyImports({ compact = false }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/import/mine").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const retry = async (j) => { if (await retryImport(j.job_id)) window.location.href = `/import?job=${j.job_id}`; };
  if (!rows || rows.length === 0) return null;
  const list = compact ? rows.slice(0, 3) : rows;

  return (
    <section className={compact ? "card-recipe !p-5 mb-10 max-w-2xl" : "mb-8 rounded-2xl border border-slate-200 bg-white p-5"} data-testid={compact ? "dashboard-my-imports" : "import-my-imports"}>
      <p className="flex items-center gap-2 font-display font-semibold text-rf-ink mb-1">
        <ClockCounterClockwise size={18} className="text-rf-orange" /> My imports
      </p>
      <p className="text-xs text-rf-ink-soft mb-3">Your imports from the last 48 hours — reopen any to review or save.</p>
      <ul className="divide-y divide-slate-100">
        {list.map((j) => (
          <li key={j.job_id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`my-import-${j.job_id}`}>
            <span className="font-semibold">From {j.source_label}</span>
            <span className="text-xs text-rf-ink-soft">{j.count} recipe{j.count === 1 ? "" : "s"} · {ago(j.created_at)}</span>
            <span className={`text-xs font-bold ${tone(j)}`}>{label(j)}</span>
            <span className="ml-auto flex gap-3">
              {j.status === "error" && j.retryable && (
                <button type="button" onClick={() => retry(j)} className="text-xs font-bold text-rf-orange hover:underline" data-testid={`my-import-retry-${j.job_id}`}>Try again</button>
              )}
              {j.committed && j.cookbook_id && (
                <Link to={`/cb/${j.cookbook_id}`} className="text-xs font-bold text-emerald-700 hover:underline" data-testid={`my-import-cookbook-${j.job_id}`}>Open cookbook</Link>
              )}
              <a href={`/import?job=${j.job_id}`} className="text-xs font-bold text-rf-orange hover:underline" data-testid={`my-import-open-${j.job_id}`}>Reopen →</a>
            </span>
          </li>
        ))}
      </ul>
      {compact && rows.length > 3 && <Link to="/import" className="text-xs font-bold text-rf-orange hover:underline mt-2 inline-block" data-testid="dashboard-my-imports-all">See all {rows.length} →</Link>}
    </section>
  );
}
